import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'

export interface ContractAlertEmailRecipient {
    id: number
    name: string
    email: string
}

export interface ContractAlertEmailSettings {
    id: number
    organization: number
    apartment: number | null
    enabled: boolean
    days_before: number[]
    include_property_manager: boolean
    recipient_users: ContractAlertEmailRecipient[]
    extra_emails: string[]
    last_sent_on: string | null
    inherited?: boolean
}

export interface UpdateContractAlertEmailSettings {
    enabled?: boolean
    days_before?: number[]
    include_property_manager?: boolean
    recipient_user_ids?: number[]
    extra_emails?: string[]
}

const QUERY_ROOT = ['contractAlertEmailSettings'] as const

const buildQuery = (organization: number | null, apartment: number | null) => {
    const params = new URLSearchParams()
    if (organization != null) {
        params.append('organization', String(organization))
    }
    if (apartment != null) {
        params.append('apartment', String(apartment))
    }
    const qs = params.toString()
    return qs ? `?${qs}` : ''
}

export const useContractAlertEmailSettings = (options: {
    organization: number | null
    apartment?: number | null
    enabled?: boolean
}) => {
    const queryClient = useQueryClient()
    const apartment = options.apartment ?? null
    const query = buildQuery(options.organization, apartment)
    const enabled = options.enabled !== false && options.organization != null

    const { data, isLoading, refetch } = useQuery<
        ContractAlertEmailSettings,
        Error
    >({
        queryKey: [...QUERY_ROOT, options.organization, apartment],
        queryFn: async () => {
            const res = await axiosInstance.get<ContractAlertEmailSettings>(
                `/company/contract_alert_email_settings/${query}`,
            )
            return res.data
        },
        enabled,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const updateSettings = useMutation({
        mutationFn: (body: UpdateContractAlertEmailSettings) =>
            axiosInstance.patch<ContractAlertEmailSettings>(
                `/company/contract_alert_email_settings/current/${query}`,
                body,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Alert email settings saved')
        },
        onError: (error: any) => {
            toast.error(
                error?.response?.data?.error ||
                    'Could not save alert email settings',
            )
        },
    })

    const resetToOrganization = useMutation({
        mutationFn: () =>
            axiosInstance.delete<{ reset: boolean }>(
                `/company/contract_alert_email_settings/current/${query}`,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('This property now uses the organization default')
        },
        onError: (error: any) => {
            toast.error(
                error?.response?.data?.error ||
                    'Could not reset alert email settings',
            )
        },
    })

    const sendNow = useMutation({
        mutationFn: () =>
            axiosInstance.post<{ sent: number }>(
                `/company/contract_alert_email_settings/send_now/${query}`,
            ),
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            const sent = res.data?.sent ?? 0
            toast.success(
                sent > 0
                    ? `Sent ${sent} alert email${sent === 1 ? '' : 's'}`
                    : 'No alert emails to send right now',
            )
        },
        onError: (error: any) => {
            toast.error(
                error?.response?.data?.error || 'Could not send alert emails',
            )
        },
    })

    return {
        settings: data ?? null,
        isLoading,
        refetch,
        updateSettings,
        resetToOrganization,
        sendNow,
    }
}
